import type { Socket } from 'socket.io-client';
import { IP_NOT_ALLOWED } from './httpError';

/**
 * CMS realtime channel: the authenticated Socket.IO namespace `/realtime`.
 *
 * It lives on the same server and Engine.IO path as the device namespace, so
 * the device socket settings are reused. socket.io-client is loaded on first
 * connect to keep it out of the initial bundle.
 */
const SOCKET_URL = import.meta.env.VITE_DEVICE_SOCKET_URL ?? '';
const SOCKET_PATH = import.meta.env.VITE_DEVICE_SOCKET_PATH ?? '/socket.io/';

export type RealtimeHandler = (payload: unknown) => void;

export interface RealtimeConnection {
	on(event: string, handler: RealtimeHandler): void;
	/** Fired when the server closed the connection (expired or invalid token). */
	onServerDisconnect(handler: () => void): void;
	/** Reconnect with the token the connection was opened with. */
	reconnect(): void;
	/** Release this handle: its handlers go; the socket closes with the last. */
	close(): void;
}

/** One socket per access token, shared by every open handle. */
interface SharedSocket {
	token: string;
	socket: Promise<Socket>;
	handles: number;
}

let shared: SharedSocket | null = null;

async function connect(token: string): Promise<Socket> {
	const { io } = await import('socket.io-client');
	const origin = SOCKET_URL || window.location.origin;
	const socket: Socket = io(`${origin}/realtime`, {
		path: SOCKET_PATH,
		auth: { token },
		transports: ['polling', 'websocket'],
		reconnection: true,
		reconnectionDelay: 1000,
		reconnectionDelayMax: 30000,
		withCredentials: false,
	});

	socket.on('connect_error', (error) => {
		// The workspace's IP allowlist refused this network: retrying cannot help.
		if (error.message === IP_NOT_ALLOWED) socket.disconnect();
	});
	return socket;
}

/**
 * Open a handle on the connection authenticated with `token`. Features share
 * one socket per token; a token change means a new connection, since the
 * server binds the socket to the token's user at handshake.
 */
export async function openRealtime(token: string): Promise<RealtimeConnection> {
	if (shared?.token !== token) {
		const entry: SharedSocket = { token, socket: connect(token), handles: 0 };
		// A failed load must not poison later attempts.
		entry.socket.catch(() => {
			if (shared === entry) shared = null;
		});
		shared = entry;
	}
	const entry = shared;
	entry.handles += 1;

	let socket: Socket;
	try {
		socket = await entry.socket;
	} catch (error) {
		entry.handles -= 1;
		throw error;
	}

	const registered: Array<[string, (...args: unknown[]) => void]> = [];
	const listen = (event: string, handler: (...args: unknown[]) => void) => {
		socket.on(event, handler);
		registered.push([event, handler]);
	};
	let closed = false;

	return {
		on: (event, handler) => listen(event, handler),
		onServerDisconnect: (handler) => {
			listen('disconnect', (reason) => {
				// socket.io does not reconnect after a server-side close.
				if (reason === 'io server disconnect') handler();
			});
		},
		reconnect: () => {
			if (!socket.connected) socket.connect();
		},
		close: () => {
			if (closed) return;
			closed = true;
			for (const [event, handler] of registered) socket.off(event, handler);
			entry.handles -= 1;
			if (entry.handles > 0) return;
			if (shared === entry) shared = null;
			socket.removeAllListeners();
			socket.disconnect();
		},
	};
}
